const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

class MCPService {

  static internalTools = [
    {
      "id": "requisao_http",
      "name": "Requisão HTTP",
      "description": "Integre seu sistema através de uma API para chamadas HTTP.",
      "provider": "PlatformHttpTools",
      "provider_title": "Solteozap IA",
      "provider_icon": "https://cdn-icons-png.freepik.com/512/7749/7749556.png",
      "variables": [],
      "version": "1.0.0",
      "tags": ["integração", "api", "conexão", "infinitagents"],
      "type": "intern",
      "enabled": true,
      "mcp_server_url": null,
      "depedencies": [],
      "form": [
        {
          "id": "name",
          "label": "De um nome para esta capacidade.",
          "value": "name",
          "required": true,
          "is_password": false,
        },
        {
          "id": "description",
          "label": "Descrição completa desta capacidade para o seu agente.",
          "value": "description",
          "required": true,
          "is_password": false,
        },
        {
          "id": "url",
          "label": "URL",
          "value": "url",
          "required": true,
          "is_password": false,
        },
        {
          "id": "method",
          "label": "Método",
          "value": "method",
          "required": true,
          "is_password": false,
          "type": "select",
          "options": [
            { "value": "GET", "label": "GET" },
            { "value": "POST", "label": "POST" },
            { "value": "PUT", "label": "PUT" },
            { "value": "DELETE", "label": "DELETE" }
          ]
        },
        {
          "id": "headers",
          "label": "Headers",
          "value": "headers",
          "required": false,
          "is_password": false,
          "type": "json"
        },
        {
          "id": "parameters_schema",
          "label": "Schema de parâmetros que sua API está esperando do agente.",
          "value": "parameters_schema",
          "required": true,
          "is_password": false,
          "type": "build_parameters"
        }
      ],
      "tool_schema": {
        "name": "{{form_name}}",
        "description": "{{form_description}}",
        "parameters": "{{form_build_parameters}}"
      }
    },
     {
    "id": "handoff_interno",
    "name": "Transferir Atendimento",
    "description": "Transfere a conversa para um atendente humano ou outra plataforma.",
    "provider": "PlatformHandoff",
    "provider_title": "QuoraAI",
    "provider_icon": "https://cdn-icons-png.flaticon.com/512/11204/11204963.png", 
    "variables": [],
    "version": "1.0.0",
    "tags": ["handoff", "transferência", "humano", "quoraai"],
    "type": "intern",
    "enabled": true,
    "mcp_server_url": null,
    "depedencies": [],
    "form": [],
    "tool_schema": {
      "name": "transferencia_para_atendente",
      "description": "Transfere o atendimento para um atendente humano, com opção de registrar o motivo da transferência.",
      "parameters": {
        "type": "object",
        "properties": {
          "motivo": {
            "type": "string",
            "description": "Motivo da transferência do atendimento (opcional)."
          }
        },
        "required": []
      }
    }
  }
  ];

  static getInternalTools() {
    return this.internalTools;
  }

  static getToolById(toolId) {
    return this.internalTools.find(tool => tool.id === toolId);
  }

  /**
   * Fetches available MCP tools (active only)
   * @param {string} token - Authentication token
   * @returns {Promise<Array>} List of active MCP tools
   */
  static async getMCPTools(token) {
    try {
      const response = await fetch(`${API_BASE_URL}/mcp-tools`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Ensures it returns an array
      const mcpTools = Array.isArray(data.data) ? data.data : [];
      
      // Processes MCP tools to expected format
      return mcpTools.map(tool => ({
        id: tool.id,
        name: tool.name,
        description: tool.description,
        type: "mcp", // Always mcp
        provider: "MCPServer", // Always MCPServer
        provider_title: tool.provider_title,
        provider_icon: tool.provider_icon,
        version: tool.version,
        tags: tool.tags || [],
        enabled: tool.enabled,
        mcp_server_url: tool.mcp_server_url,
        form: tool.form || [],
        depedencies: [], // MCP tools don't have dependencies yet
        tool_schema: null, // MCP doesn't use static tool_schema
        created_at: tool.created_at,
        updated_at: tool.updated_at
      }));

    } catch (error) {
      console.error('Error fetching MCP tools:', error);
      // In case of error, returns empty array to not break the interface
      return [];
    }
  }

  /**
   * Checks if a tool can be added based on its dependencies
   * @param {Object} tool - The tool to be checked
   * @param {Array} currentTools - List of tools already added to the agent
   * @param {Object} userIntegrations - Object with user integrations
   * @returns {Object} - Object with verification result {canAdd: boolean, alert: Object}
   */
  static checkToolDependencies(tool, currentTools = [], userIntegrations = {}) {
    // MCP tools don't have dependencies yet
    if (tool.type === 'mcp') {
      return { canAdd: true };
    }

    // If there are no dependencies, the tool can be added
    if (!tool.depedencies || tool.depedencies.length === 0) {
      return { canAdd: true };
    }

    // Check each dependency
    for (const dependency of tool.depedencies) {
      // Check dependency for a specific tool
      if (dependency.type === 'tool') {
        const requiredToolId = dependency.tool_id;
        const hasTool = currentTools.some(t => t.id === requiredToolId);
        
        if (!hasTool && dependency.required) {
          return {
            canAdd: false,
            alert: dependency.alert || {
              alert_title: "Dependency not found",
              alert_description: `This tool requires the tool "${this.internalTools.find(t => t.id === requiredToolId)?.name || requiredToolId}" which is not added.`,
              show_add_button: true,
              add_tool_id: requiredToolId
            }
          };
        }
      }

      if (dependency.type === 'integration') {
        const integrationKey = dependency.integration_key;
        const hasIntegration = userIntegrations[integrationKey] && 
                               userIntegrations[integrationKey].status === 'active';
       
        if (!hasIntegration && dependency.required) {
          return {
            canAdd: false,
            alert: dependency.alert || {
              alert_title: "Integration required",
              alert_description: `This tool requires the integration "${integrationKey}" which is not active.`,
              go_route: dependency.alert?.go_route || "account"
            }
          };
        }
      }
    }
    
    // If all dependencies have been met or none are required
    return { canAdd: true };
  }

  /**
   * Pre-processes and validates a tool before being displayed in the selection list
   * @param {Array} tools - List of available tools
   * @param {Array} currentTools - List of tools already added to the agent
   * @returns {Array} - List of processed tools with availability status
   */
  static async getAvailableTools(tools = this.internalTools, currentTools = [], token) {
    const mcpTools = await this.getMCPTools(token)

    let allTools = [...mcpTools, ...tools.map(tool => {
      const dependencyCheck = this.checkToolDependencies(tool, currentTools);
      
      return {
        ...tool,
        available: dependencyCheck.canAdd,
        alert: dependencyCheck.canAdd ? null : dependencyCheck.alert
      };
    })]

    return allTools;
  }

  static convertToolForPlatform(internalTool, formValues) {
    // Para canais de comunicação, os formValues já vêm estruturados
    if (internalTool.type === 'communication-channel') {
      return {
        id: internalTool.id,
        name: internalTool.name,
        description: internalTool.description,
        type: internalTool.type,
        provider: internalTool.provider,
        provider_title: internalTool.provider_title,
        provider_icon: internalTool.provider_icon,
        version: internalTool.version,
        tags: internalTool.tags,
        depedencies: internalTool.depedencies,
        enabled: internalTool.enabled,
        form_values: formValues.form_values || formValues,
        channel_config: internalTool.channel_config,
        connection_schema: internalTool.connection_schema
      };
    }

    const filteredValues = Object.entries(formValues).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});

    return {
      id: internalTool.id,
      name: internalTool.name,
      description: internalTool.description,
      type: internalTool.type,
      provider: internalTool.provider,
      provider_title: internalTool.provider_title,
      provider_icon: internalTool.provider_icon,
      version: internalTool.version,
      tags: internalTool.tags,
      depedencies: internalTool.depedencies,
      enabled: internalTool.enabled,
      form_values: filteredValues,
      tool_schema: internalTool.tool_schema,
      mcp_server_url: internalTool.type === 'mcp' ? internalTool.mcp_server_url : null
    };
  }
}

export default MCPService;